import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import Header from './Header';
import { useNavigate } from 'react-router-dom';
import productsPricing from '../data/productsPricing';
import './LandingPage.css';
import companyLogo from '../images/B_longtermtec_logo.png';
import cartIcon from '../images/shopping-cart.svg';
import {
    DEFAULT_LOCALE,
    DEFAULT_CURRENCY,
    normalizeLocale,
    findCurrencySymbol,
    getStoreTemplateAttributes
} from '../utils/localeUtils';

const LandingPage = ({ locale: propLocale }) => {
    const navigate = useNavigate();
    const [locale, setLocale] = useState(propLocale || DEFAULT_LOCALE);
    const [currencySymbol, setCurrencySymbol] = useState(DEFAULT_CURRENCY);
    const [products, setProducts] = useState([]);
    const [cartCount, setCartCount] = useState(0);
    const [activeCategory, setActiveCategory] = useState('all');
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOption, setSortOption] = useState('price-desc');
    const [subscriptionFilter, setSubscriptionFilter] = useState('all');
    const tabsContainerRef = useRef(null);

    // Memoized store template attributes to avoid repeated DOM queries
    const storeTemplateAttributes = useMemo(() => getStoreTemplateAttributes(), []);

    // Initialize locale and currency on component mount
    useEffect(() => {
        // Use prop locale if provided, otherwise detect from browser or store template
        const detectedLocale = propLocale || storeTemplateAttributes.locale ||
            (productsPricing[normalizeLocale(navigator.language)] ? normalizeLocale(navigator.language) : DEFAULT_LOCALE);

        setLocale(detectedLocale);
        setCurrencySymbol(findCurrencySymbol(detectedLocale));
        setProducts(productsPricing[detectedLocale] || []);
    }, [propLocale, storeTemplateAttributes.locale]);

    // Initialize SPI from store template
    useEffect(() => {
        if (storeTemplateAttributes.spi) {
            sessionStorage.setItem('spi', storeTemplateAttributes.spi);
        }
    }, [storeTemplateAttributes.spi]);

    // Check cart count on mount and when storage changes
    useEffect(() => {
        const updateCartCount = () => {
            const storedCart = JSON.parse(sessionStorage.getItem('cart')) || [];
            setCartCount(storedCart.length);
        };

        updateCartCount();

        // Listen for storage changes (for when items are added from other pages)
        window.addEventListener('storage', updateCartCount);
        return () => window.removeEventListener('storage', updateCartCount);
    }, []);

    const handleViewCart = useCallback(() => {
        navigate(`/cart-summary?locale=${locale}`);
    }, [navigate, locale]);

    // Track product clicks and navigate to cart
    const handleViewDetails = useCallback((sku) => {
        const clickCount = parseInt(sessionStorage.getItem(sku) || '0', 10);
        sessionStorage.setItem(sku, (clickCount + 1).toString());

        // Save scroll position before navigating
        sessionStorage.setItem('landingPageScrollPosition', window.scrollY.toString());

        const spi = sessionStorage.getItem('spi');
        navigate(`/cart/${sku}?spi=${spi}&locale=${locale}`);
    }, [navigate, locale]);

    // Restore scroll position when returning to this page (after products are loaded)
    useEffect(() => {
        if (products.length > 0) {
            const savedScrollPosition = sessionStorage.getItem('landingPageScrollPosition');
            if (savedScrollPosition) {
                // Use requestAnimationFrame to ensure DOM has updated
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        window.scrollTo(0, parseInt(savedScrollPosition, 10));
                        sessionStorage.removeItem('landingPageScrollPosition');
                    });
                });
            }
        }
    }, [products]);

    // Group products by SKU prefix (trials are excluded from the catalog and surfaced via direct links)
    const solidEdgeProducts = useMemo(() => {
        return products
            .filter(p => p.sku.startsWith('SE') && !p.isTrial)
            .sort((a, b) => (b.priceYearly || 0) - (a.priceYearly || 0));
    }, [products]);

    const nxProducts = useMemo(() => {
        return products
            .filter(p => p.sku.startsWith('NXX') && !p.isTrial)
            .sort((a, b) => (b.priceYearly || 0) - (a.priceYearly || 0));
    }, [products]);

    // Category definitions (product line)
    const categories = useMemo(() => [
        { id: 'all', label: 'All Products' },
        { id: 'solidedge', label: 'Designcenter Solid Edge' },
        { id: 'nxx', label: 'Designcenter X NX' },
    ], []);


    // Subscription filter definitions
    const subscriptionFilters = useMemo(() => [
        { id: 'all', label: 'All' },
        { id: 'monthly', label: 'Base Products - Monthly / Annual / 3-Year' },
        { id: 'standalone', label: 'Base Products - Annual / 3-Year' },
        { id: 'addons', label: 'Add-ons & Extensions' },
    ], []);

    // Get products based on active category
    const categoryProducts = useMemo(() => {
        switch (activeCategory) {
            case 'solidedge':
                return solidEdgeProducts;
            case 'nxx':
                return nxProducts;
            case 'all':
            default:
                return [...solidEdgeProducts, ...nxProducts];
        }
    }, [activeCategory, solidEdgeProducts, nxProducts]);

    // Filter products by subscription type
    const subscriptionFilteredProducts = useMemo(() => {
        switch (subscriptionFilter) {
            case 'monthly':
                // Base Products - Monthly / Annual / 3-Year (has monthly price, not an addon)
                return categoryProducts.filter(p => p.priceMonthly > 0 && !p.isAddon && !p.isTrial);
            case 'standalone':
                // Base Products - Annual / 3-Year (no monthly price, not an addon)
                return categoryProducts.filter(p => (!p.priceMonthly || p.priceMonthly === 0) && !p.isAddon && !p.isTrial);
            case 'addons':
                // Add-ons & Extensions
                return categoryProducts.filter(p => p.isAddon);
            case 'all':
            default:
                return categoryProducts;
        }
    }, [categoryProducts, subscriptionFilter]);

    // Filter products by search query
    const filteredProducts = useMemo(() => {
        if (!searchQuery.trim()) {
            return subscriptionFilteredProducts;
        }
        const query = searchQuery.toLowerCase();
        return subscriptionFilteredProducts.filter(p =>
            p.name.toLowerCase().includes(query) ||
            p.sku.toLowerCase().includes(query)
        );
    }, [subscriptionFilteredProducts, searchQuery]);

    // Sort products
    const displayedProducts = useMemo(() => {
        const sorted = [...filteredProducts];
        switch (sortOption) {
            case 'price-asc':
                return sorted.sort((a, b) => (a.priceYearly || 0) - (b.priceYearly || 0));
            case 'price-desc':
                return sorted.sort((a, b) => (b.priceYearly || 0) - (a.priceYearly || 0));
            case 'name-asc':
                return sorted.sort((a, b) => a.name.localeCompare(b.name));
            case 'name-desc':
                return sorted.sort((a, b) => b.name.localeCompare(a.name));
            default:
                return sorted;
        }
    }, [filteredProducts, sortOption]);

    // Check if arrows should be shown
    const checkArrows = useCallback(() => {
        const container = tabsContainerRef.current;
        if (container) {
            setShowLeftArrow(container.scrollLeft > 0);
            setShowRightArrow(
                container.scrollLeft < container.scrollWidth - container.clientWidth - 1
            );
        }
    }, []);

    // Initialize arrow visibility
    useEffect(() => {
        checkArrows();
        window.addEventListener('resize', checkArrows);
        return () => window.removeEventListener('resize', checkArrows);
    }, [checkArrows, categories]);

    // Scroll tabs
    const scrollTabs = useCallback((direction) => {
        const container = tabsContainerRef.current;
        if (container) {
            const scrollAmount = 200;
            container.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
            setTimeout(checkArrows, 300);
        }
    }, [checkArrows]);

    const renderProductCard = (product) => (
        <div
            key={product.sku}
            className="card"
            onClick={() => handleViewDetails(product.sku)}
        >
            <img
                src={product.image}
                alt={product.name}
                className="image"
            />
            <h3>{product.name}</h3>
            <p className="price">
                {product.isTrial
                    ? "30 Days Free Trial"
                    : (product.priceMonthly > 0
                        ? `From ${currencySymbol} ${product.priceMonthly.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} for 1 month.`
                        : `From ${currencySymbol} ${product.priceYearly.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} for 1 year.`
                    )
                }
            </p>
        </div>
    );

    return (
        <div className="landing-page">
            <Header logo={companyLogo} />

            {cartCount > 0 && (
                <div className="floating-cart" onClick={handleViewCart}>
                    <img src={cartIcon} alt="Cart" className="cart-icon" />
                    <span className="cart-badge">{cartCount}</span>
                    <span className="cart-label">View Cart</span>
                </div>
            )}

            {/* Category Tabs */}
            <div className="category-tabs-wrapper">
                {showLeftArrow && (
                    <button
                        className="tab-arrow tab-arrow-left"
                        onClick={() => scrollTabs('left')}
                        aria-label="Scroll left"
                    >
                        &#8249;
                    </button>
                )}
                <div
                    className="category-tabs"
                    ref={tabsContainerRef}
                    onScroll={checkArrows}
                >
                    {categories.map(category => (
                        <button
                            key={category.id}
                            className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
                            onClick={() => setActiveCategory(category.id)}
                        >
                            {category.label}
                        </button>
                    ))}
                </div>
                {showRightArrow && (
                    <button
                        className="tab-arrow tab-arrow-right"
                        onClick={() => scrollTabs('right')}
                        aria-label="Scroll right"
                    >
                        &#8250;
                    </button>
                )}
            </div>

            {/* Subscription Filter Pills */}
            <div className="subscription-filters">
                {subscriptionFilters.map(filter => (
                    <button
                        key={filter.id}
                        className={`subscription-pill ${subscriptionFilter === filter.id ? 'active' : ''}`}
                        onClick={() => setSubscriptionFilter(filter.id)}
                    >
                        {filter.label}
                    </button>
                ))}
            </div>

            {/* Search and Sort Bar */}
            <div className="search-sort-bar">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Search products..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-input"
                    />
                    {searchQuery && (
                        <button
                            className="search-clear"
                            onClick={() => setSearchQuery('')}
                            aria-label="Clear search"
                        >
                            ×
                        </button>
                    )}
                </div>
                <div className="sort-box">
                    <label htmlFor="sort-select">Sort by:</label>
                    <select
                        id="sort-select"
                        value={sortOption}
                        onChange={(e) => setSortOption(e.target.value)}
                        className="sort-select"
                    >
                        <option value="price-desc">Price: High to Low</option>
                        <option value="price-asc">Price: Low to High</option>
                        <option value="name-asc">Name: A - Z</option>
                        <option value="name-desc">Name: Z - A</option>
                    </select>
                </div>
            </div>

            {/* Products Grid */}
            <section className="product-section">
                <div className="grid">
                    {displayedProducts.map(renderProductCard)}
                </div>
                {displayedProducts.length === 0 && (
                    <p className="no-products">
                        {searchQuery ? `No products found for "${searchQuery}"` : 'No products available in this category.'}
                    </p>
                )}
            </section>

        </div>
    );
};

export default LandingPage;
